/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import Select, { SelectPresentationProps, SelectProps } from "@eisinc/MobiusOverrides/Select";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    country: state.components.contactUsForm.fieldValues["country"],
    countries: getCurrentCountries(state),
});

const mapDispatchToProps = {
    setFieldValue,
    loadCurrentCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CountryFieldStyles {
    countrySelectField?: SelectPresentationProps;
}

export const countryFieldStyles: CountryFieldStyles = {
    countrySelectField: {
        cssOverrides: {
            formField: css`
                margin-bottom: 15px;
                width: 45%;
                @media (max-width: 768px) {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = countryFieldStyles;

const CountryField: React.FC<Props> = ({ fields, country, countries, setFieldValue, loadCurrentCountries }) => {
    React.useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
    }, []);

    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [countryErrorMessage, setCountryErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.country = validateCountry;
        if (typeof country !== "undefined") {
            validateCountry();
        }
    }, [country]);

    React.useEffect(() => {
        contactFormContext.validators.country = validateCountry;
    }, [isRequired]);

    const validateCountry = () => {
        const errorMessage = isRequired && !country ? (siteMessage("Field_Required", label) as string) : "";
        setCountryErrorMessage(errorMessage);
        return !errorMessage;
    };

    const handleCountryChange: SelectProps["onChange"] = event => {
        const country = countries ? countries?.find(c => c.abbreviation === event.target.value) : undefined;
        setFieldValue({ key: "country", value: country?.abbreviation || "" });
    };

    return (
        <Select
            {...styles.countrySelectField}
            label={label}
            required={isRequired}
            value={country || ""}
            onChange={handleCountryChange}
            error={countryErrorMessage}
        >
            <option value="">{hintText}</option>
            {countries &&
                countries.map(country => (
                    <option key={country.id} value={country.abbreviation}>
                        {country.name}
                    </option>
                ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CountryField),
    definition: {
        group: "Contact Us",
        icon: "FormField",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select Country",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select Country",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
